/*
 * Copyright (c) 2025. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { FormControlLabel, Typography } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { IOSSwitch } from '../IOSSwitch';
import { useFieldContext } from './formContext';

type Props = {
  label?: React.ReactNode;
  fontSize?: number;
  disabled?: boolean;
  onChange?: (value: boolean) => void;
  invert?: boolean;
};

function FormSwitch({ label, disabled, fontSize = 14, onChange, invert = false }: Readonly<Props>) {
  const field = useFieldContext<boolean>();
  const { name } = field;
  const value = invert ? !field.state.value : field.state.value;

  const handleChange = useCallback(() => {
    const newValue = invert ? value : !value;
    field.handleChange(newValue);
    onChange?.(newValue);
  }, [onChange, value, invert, field]);

  const error = useMemo(() => field.state.meta.errors[0]?.message, [field.state.meta.errors]);

  return (
    <>
      {label ? (
        <FormControlLabel
          control={<IOSSwitch checked={value} onChange={handleChange} data-testid={name} />}
          label={
            <Typography
              sx={{
                color: value ? undefined : 'text.secondary',
                fontSize,
                lineHeight: 1.1,
              }}
            >
              {label}
            </Typography>
          }
          disabled={disabled}
          sx={{ ml: 0 }}
        />
      ) : (
        <IOSSwitch checked={value} onChange={handleChange} data-testid={name} disabled={disabled} />
      )}
      {error && (
        <Typography
          sx={{
            fontSize: 13,
            color: 'error.main',
            mt: 0.5,
          }}
        >
          {error}
        </Typography>
      )}
    </>
  );
}

export default FormSwitch;
